import {
  getAuth,
  onIdTokenChanged,
  signOut
} from 'firebase/auth';
import { FieldPath, doc, getFirestore, onSnapshot, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { node } from 'prop-types';
import { createContext, useEffect, useReducer, useState } from 'react';

import accounts from '...data/accounts/collections/accounts';
import members from '...data/members/collections/members';
import subscriptions from '...data/subscriptions/collections/subscriptions';
import { app } from '...modules/firebase/web';

// ----------------------------------------------------------------------

const auth = getAuth(app);
const firestore = getFirestore(app);
const functions = getFunctions(app);
const getIntegrationsCallable = httpsCallable(functions, 'integrationsGet');
const getKeysCallable = httpsCallable(functions, 'keysGet');

// SECURITY (Aug 5): integrations are no longer read directly from Firestore
// client-side (firestore.rules now blocks all client reads on that
// collection, since the raw doc previously exposed the plaintext Google
// service-account credential to any account member). This polls the
// `integrationsGet` callable instead, which strips the credential
// server-side before returning anything. Polling, not onSnapshot, since
// callable functions aren't real-time — 20s is a reasonable interim
// interval for a settings-style page; revisit if this needs to feel
// more live later.
const INTEGRATIONS_POLL_MS = 20000;

// SECURITY (Aug 5): same reasoning as above — a key's document ID *is* the
// actual secret, so direct Firestore reads on `keys` are now blocked too.
// Polls `keysGet` instead, which masks secret keys for non-admin members.
const KEYS_POLL_MS = 20000;

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  roles: null
};

const reducer = (state, action) => {
  console.log('reducer called with:', action);
  if (action.type === 'LOGGED_OUT') {
    return {
      ...state,
      isAuthenticated: false,
      isInitialized: true,
      user: null,
      roles: null
    };
  }
  if (action.type === 'LOGGING_IN') {
    const { user, roles } = action.payload;
    return {
      ...state,
      isAuthenticated: false,
      isInitialized: false,
      user,
      roles
    };
  }
  if (action.type === 'LOGGED_IN') {
    return {
      ...state,
      isAuthenticated: true,
      isInitialized: true
      // Possibly other initial data here.
    };
  }

  return state;
};

const AuthContext = createContext({
  ...initialState,
  method: 'firebase',
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  logout: () => Promise.resolve()
});

// TODO: Revise this based on user role.
const observeAccount = (accountId, onData) => {
  if (!accountId) {
    return;
  }
  const da = doc(firestore, 'accounts', accountId);
  const qm = query(members, where('entity', '==', accountId));
  const qs = query(subscriptions, where('account', '==', accountId));

  let _account = null;
  let _accountError = null;
  let _keys = [];
  let _members = [];
  let _membersError = null;
  let _subscriptions = [];
  let _subscriptionsError = null;
  let _integrations = [];
  // let customer = null; // TODO: Get billing stuff from Stripe Firebase Extension.

  let initAccount = false;
  let initKeys = false;
  let initMembers = false;
  let initSubscriptions = false;
  let initIntegrations = false;

  const callOnData = () => {
    if (initAccount && initKeys && initMembers && initSubscriptions && initIntegrations) {
      // Call after they all run the first time.
      onData({
        account: _account,
        accountError: _accountError,
        keys: _keys,
        members: _members,
        membersError: _membersError,
        subscriptions: _subscriptions,
        subscriptionsError: _subscriptionsError,
        integrations: _integrations
      });
    }
  };

  let stops = [
    // Account doc
    onSnapshot(da, (doc) => {
      _account = { id: doc.id, ...doc.data() };
      _accountError = null;
      initAccount = true;
      callOnData();
    }, (error) => {
      // FIX (Aug 7): previously no error handler at all — a permission
      // error here meant this callback never fired, initAccount stayed
      // false forever, and callOnData()'s all-flags gate never opened,
      // even if keys/integrations/members/subscriptions all succeeded.
      // That looked like an infinite hang with zero indication why.
      console.log('Account listener failed:', { code: error.code, message: error.message });
      _accountError = { code: error.code, message: error.message };
      initAccount = true;
      callOnData();
    }),
    // Members for account
    onSnapshot(qm, (snapshot) => {
      _members = [];
      snapshot.forEach((doc) => _members.push({ id: doc.id, ...doc.data() }));
      _membersError = null;
      initMembers = true;
      callOnData();
    }, (error) => {
      console.log('Members listener failed:', { code: error.code, message: error.message });
      _membersError = { code: error.code, message: error.message };
      initMembers = true;
      callOnData();
    }),
    // Subscriptions for account
    onSnapshot(qs, (snapshot) => {
      _subscriptions = [];
      snapshot.forEach((doc) => _subscriptions.push({ id: doc.id, ...doc.data() }));
      _subscriptionsError = null;
      initSubscriptions = true;
      callOnData();
    }, (error) => {
      console.log('Subscriptions listener failed:', { code: error.code, message: error.message });
      _subscriptionsError = { code: error.code, message: error.message };
      initSubscriptions = true;
      callOnData();
    }),
  ];

  // Keys for account — via sanitized callable, not a direct Firestore read.
  // See SECURITY note above.
  const fetchKeys = () => {
    getKeysCallable({ accountId })
      .then((result) => {
        _keys = result?.data?.keys || [];
        initKeys = true;
        callOnData();
      })
      .catch((error) => {
        console.log('Error fetching keys:', error);
        initKeys = true;
        callOnData();
      });
  };

  fetchKeys();
  const keysPollId = setInterval(fetchKeys, KEYS_POLL_MS);

  // Integrations for account — via sanitized callable, not a direct Firestore
  // read. See SECURITY note above.
  const fetchIntegrations = () => {
    getIntegrationsCallable({ accountId })
      .then((result) => {
        _integrations = result?.data?.integrations || [];
        initIntegrations = true;
        callOnData();
      })
      .catch((error) => {
        console.log('Error fetching integrations:', error);
        // Still mark as initialized so callOnData isn't permanently blocked
        // by a transient failure.
        initIntegrations = true;
        callOnData();
      });
  };

  fetchIntegrations();
  const integrationsPollId = setInterval(fetchIntegrations, INTEGRATIONS_POLL_MS);

  return () => {
    stops.forEach((stop) => stop());
    clearInterval(integrationsPollId);
    clearInterval(keysPollId);
  };
};

const observeMembership = (uid, onData) => {
  const qm = query(members, where('user.uid', '==', uid));
  return onSnapshot(qm, (snapshot) => {
    const _members = [];
    snapshot.forEach((doc) => _members.push(doc.data()));
    onData(_members);
  });
};

const observeAccounts = (_members, onData) => {
  const accountIds = _members.filter((m) => m.entity !== 'global').map((m) => m.entity);
  const qa = query(accounts, where(FieldPath.documentId(), 'in', accountIds));
  return onSnapshot(qa, (snapshot) => {
    const _accounts = [];
    snapshot.forEach((doc) => _accounts.push(doc.data()));
    onData(_accounts);
  });
};

// ----------------------------------------------------------------------

// const path = 'users';
// console.log('watching rtdb path:', path);
// const dbRef = ref(database, path);
// onValue(dbRef, (snapshot) => {
//   console.log('rtdb snapshot', snapshot.val());
//   // if (snapshot.val()) {
//   //   console.log('id token updated...force refreshing token');
//   // }
// });

AuthProvider.propTypes = {
  children: node
};

// "Initialize" procedure requires the following:
// - User logs in. Turn OFF isInitialized, to show loading screen.
// - Data is acquired. AT LEAST: membership (must be member of one account) and account
// - If registration is in progress, DO NOT allow dispatching isAuthenticated from onAuthStateChanged.
//   Wait until the documents have been created on the server. Load initial data,
//   and then dispatch from register.
// - If registration is NOT in progress, load initial data, and dispatch from onAuthStateChanged.

let registering = false;
let lastUserId;
const initAccountData = {
  account: null,
  accountError: null,
  keys: [],
  members: [],
  membersError: null,
  subscriptions: [],
  subscriptionsError: null,
  integrations: []
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const [accountData, setAccountData] = useState(initAccountData);

  // From Member doc for current active Account ()
  // const [profile, setProfile] = useState(null);

  useEffect(
    () =>
      onIdTokenChanged(auth, async (user) => {
        console.log('auth state changed', user);
        if (user) {
          const { claims } = await user.getIdTokenResult();
          console.log('User ID:', user.uid);
          console.log('User claims:', claims);
          if (!registering && user.uid !== lastUserId) {
            dispatch({
              type: 'LOGGING_IN',
              payload: { user, roles: claims.roles || null }
            });
            lastUserId = user.uid;
          }
        } else {
          dispatch({
            type: 'LOGGED_OUT'
          });
          lastUserId = null;
          setAccountData(initAccountData);
        }
      }),
    [dispatch]
  );

  const accountId = Object.keys(state?.roles || {}).filter((id) => id !== 'global')[0];

  useEffect(() => {
    if (accountId) {
      console.log('Account ID', accountId);
      const stop = observeAccount(accountId, (data) => {
        setAccountData(data);
        console.log('setting account data:', data);
      });
      dispatch({
        type: 'LOGGED_IN',
        payload: {
          // I guess put other initial data in here, or not
        }
      });
      return stop;
    }
  }, [accountId]);

  const register = async (providerPromise) => {
    registering = true;
    const userCredential = await providerPromise;
    registering = false;
    userCredential.user.getIdToken(true);
  };

  const logout = () => signOut(auth);

  const user = {
    id: state?.user?.uid,
    email: state?.user?.email,
    photoURL: state?.user?.photoURL,
    displayName: state?.user?.displayName,
    role: (state?.roles || {})[accountId],
    phoneNumber: state?.user?.phoneNumber
  };

  const value = {
    ...state,
    ...accountData,
    method: 'firebase',
    user,
    register,
    logout
  };

  console.log('AuthProvider value:', value);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthProvider };
